import React, { useEffect, useState } from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { getCreativeImageApi, getReferenceImageApi, type CreativeContent, type CreativeReview } from "../criativosApi";

/** Imagem do criativo: a rota exige token, então vem por `fetch` e vira object URL. */
export const CreativeImage: React.FC<{ id: string | "reference"; alt: string; className?: string; refreshKey?: string }> = ({
  id,
  alt,
  className,
  refreshKey,
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    (id === "reference" ? getReferenceImageApi() : getCreativeImageApi(id))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, refreshKey]);

  if (!src) return <div className={`${className ?? ""} crvImg--empty`} role="img" aria-label={failed ? "Imagem indisponível" : "Carregando imagem"} />;
  return <img className={className} src={src} alt={alt} />;
};

/** O que a conferência da Eco-IA achou. */
export const ReviewBox: React.FC<{ review: CreativeReview }> = ({ review }) => {
  if (!review) {
    return (
      <p className="cmpNotice cmpNotice--info">
        <EcoIcon name="eye" size={16} />
        <span>Sem conferência da Eco-IA nesta versão. Confira os textos antes de usar.</span>
      </p>
    );
  }
  if (review.approved) {
    return (
      <p className="cmpNotice crvOk">
        <EcoIcon name="check" size={16} />
        <span>
          <strong>Conferida pela Eco-IA.</strong> {review.summary}
          {review.attempts > 1 ? " (Corrigida automaticamente uma vez.)" : ""}
        </span>
      </p>
    );
  }
  return (
    <div className="cmpNotice cmpNotice--bad">
      <EcoIcon name="alertTriangle" size={16} />
      <div>
        <strong>A Eco-IA viu problemas.</strong> {review.summary}
        {review.problems.length ? (
          <ul className="crvProblems">
            {review.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        ) : null}
        <span className="cmpSub">Peça um ajuste descrevendo o que corrigir, ou refaça.</span>
      </div>
    </div>
  );
};

export const ReviewPill: React.FC<{ review: CreativeReview }> = ({ review }) =>
  !review ? (
    <span className="ecoPill ecoPill--off">Sem conferência</span>
  ) : review.approved ? (
    <span className="ecoPill">Conferido</span>
  ) : (
    <span className="ecoPill cmpPill--warn">Ver problemas</span>
  );

/** Aviso enquanto o Google desenha: leva de 1 a 3 minutos. */
export const GeneratingNotice: React.FC<{ label: string }> = ({ label }) => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const step = seconds < 70 ? "Desenhando a arte" : seconds < 100 ? "Conferindo os textos" : "Corrigindo o que a conferência apontou";
  return (
    <div className="crvGenerating" role="status" aria-live="polite">
      <span className="crvSpinner" aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <span className="cmpSub">
          {step}… {seconds}s. Leva de 1 a 3 minutos; não feche esta tela.
        </span>
      </div>
    </div>
  );
};

type FormProps = {
  value: CreativeContent;
  onChange: (c: CreativeContent) => void;
  disabled?: boolean;
};

/** Os textos do criativo, na ordem em que aparecem na arte. */
export const CreativeContentForm: React.FC<FormProps> = ({ value: c, onChange, disabled }) => {
  const set = <K extends keyof CreativeContent>(key: K, v: CreativeContent[K]) => onChange({ ...c, [key]: v });
  const text = (key: keyof CreativeContent, label: string, max: number, placeholder?: string, optional = false) => (
    <label className="ecoField">
      <span className="ecoLabel">
        {label}
        {optional ? <span className="ecoLabel__optional"> (opcional)</span> : null}
      </span>
      <input
        className="ecoInput"
        maxLength={max}
        disabled={disabled}
        value={(c[key] as string | null) ?? ""}
        placeholder={placeholder}
        onChange={(e) => set(key, e.target.value as never)}
      />
    </label>
  );

  return (
    <div className="crvForm">
      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Topo</legend>
        <div className="cmpGrid">
          {text("headline", "Título", 40)}
          {text("tagline", "Frase abaixo do título", 80)}
        </div>
      </fieldset>

      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Cargo</legend>
        <div className="cmpGrid cmpGrid--3">
          {text("roleTitle", "Cargo", 40, "Ex.: ENTREGADOR")}
          {text("roleSubtitle", "Complemento", 40, "Ex.: DE MOTO", true)}
          {text("roleIcon", "Ícone", 40, "Ex.: moto")}
        </div>
      </fieldset>

      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Benefícios (até 5)</legend>
        {c.benefits.map((b, i) => (
          <div key={i} className="crvRow">
            <input
              className="ecoInput"
              aria-label={`Benefício ${i + 1}`}
              placeholder="Ex.: DIÁRIA"
              maxLength={40}
              disabled={disabled}
              value={b.title}
              onChange={(e) => set("benefits", c.benefits.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
            />
            <input
              className="ecoInput"
              aria-label={`Valor do benefício ${i + 1}`}
              placeholder="Valor (ex.: R$ 120,00)"
              maxLength={40}
              disabled={disabled}
              value={b.value ?? ""}
              onChange={(e) => set("benefits", c.benefits.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
            />
            <input
              className="ecoInput"
              aria-label={`Complemento do benefício ${i + 1}`}
              placeholder="Complemento (ex.: por dia)"
              maxLength={40}
              disabled={disabled}
              value={b.note ?? ""}
              onChange={(e) => set("benefits", c.benefits.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
            />
            <button
              type="button"
              className="ecoBtn ecoBtn--icon"
              aria-label={`Remover benefício ${i + 1}`}
              disabled={disabled || c.benefits.length === 1}
              onClick={() => set("benefits", c.benefits.filter((_, j) => j !== i))}
            >
              <EcoIcon name="x" size={16} />
            </button>
          </div>
        ))}
        {c.benefits.length < 5 ? (
          <button type="button" className="ecoBtn crvAdd" disabled={disabled} onClick={() => set("benefits", [...c.benefits, { title: "", value: null, note: null }])}>
            <EcoIcon name="plus" size={16} />
            Benefício
          </button>
        ) : null}
        <p className="cmpHint">Só ponha valor que a empresa paga de fato: a arte mostra exatamente o que estiver aqui.</p>
      </fieldset>

      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Requisitos (até 3)</legend>
        {c.requirements.map((r, i) => (
          <div key={i} className="crvRow crvRow--one">
            <input
              className="ecoInput"
              aria-label={`Requisito ${i + 1}`}
              placeholder="Ex.: CNH categoria A."
              maxLength={80}
              disabled={disabled}
              value={r}
              onChange={(e) => set("requirements", c.requirements.map((x, j) => (j === i ? e.target.value : x)))}
            />
            <button
              type="button"
              className="ecoBtn ecoBtn--icon"
              aria-label={`Remover requisito ${i + 1}`}
              disabled={disabled || c.requirements.length === 1}
              onClick={() => set("requirements", c.requirements.filter((_, j) => j !== i))}
            >
              <EcoIcon name="x" size={16} />
            </button>
          </div>
        ))}
        {c.requirements.length < 3 ? (
          <button type="button" className="ecoBtn crvAdd" disabled={disabled} onClick={() => set("requirements", [...c.requirements, ""])}>
            <EcoIcon name="plus" size={16} />
            Requisito
          </button>
        ) : null}
      </fieldset>

      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Escala e local</legend>
        {text("schedule", "Escala de trabalho", 80, "Ex.: Fins de semana e feriados")}
        <div className="cmpGrid">
          {text("locationLine1", "Local", 80, "Ex.: Lojas da Zona Sul")}
          {text("locationLine2", "Cidade - UF", 80, "Ex.: Rio de Janeiro - RJ", true)}
        </div>
      </fieldset>

      <fieldset className="crvForm__group">
        <legend className="crvForm__legend">Chamada final</legend>
        <div className="cmpGrid cmpGrid--3">
          {text("ctaTitle", "Título", 40)}
          {text("ctaMain", "Chamada", 40)}
          {text("ctaSub", "Complemento", 80, undefined, true)}
        </div>
      </fieldset>
    </div>
  );
};
